import { runAgentLoop, type AgentLoopEvent, type AgentLoopResult, type AgentRole, type ToolExecutor, type ToolInvocation } from "@private-fiction/story-core";
import { streamNarration, type StreamEvent, type StreamRequest } from "./gateway";
import { getFreshAccessToken } from "./supabase";

export interface GatewayAgentLoopInput {
  endpoint: string;
  accessToken: string | (() => Promise<string | null> | string | null);
  request: Omit<StreamRequest, "clientRequestId" | "messages" | "agent"> & { messages: StreamRequest["messages"] };
  runId: string;
  storyId: string;
  role: AgentRole;
  phase: "planning" | "narration" | "postprocessing" | "resume";
  enabledToolIds: string[];
  executor: ToolExecutor;
  approve?: (invocation: ToolInvocation) => Promise<boolean> | boolean;
  onRequest?: (request: Omit<StreamRequest,"clientRequestId">) => void;
  onEvent?: (event: StreamEvent) => void;
  onToolReceipt?: Parameters<typeof runAgentLoop>[0]["onToolReceipt"];
  onToolStart?: Parameters<typeof runAgentLoop>[0]["onToolStart"];
  signal?: (AbortSignal) | undefined;
  maxSteps?: number;
  sourceMessageIds?: readonly string[];
  resume?: Parameters<typeof runAgentLoop>[0]['resume'];
  onCheckpoint?: Parameters<typeof runAgentLoop>[0]['onCheckpoint'];
  onRequestStart?: Parameters<typeof runAgentLoop>[0]['onRequestStart'];
}

export function runGatewayAgentLoop(input: GatewayAgentLoopInput): Promise<AgentLoopResult> {
  return runAgentLoop({
    runId: input.runId, storyId: input.storyId, role: input.role, phase: input.phase,
    enabledToolIds: input.enabledToolIds, messages: input.request.messages, executor: input.executor,
    ...(input.resume?{resume:input.resume}:{}), ...(input.onCheckpoint?{onCheckpoint:input.onCheckpoint}:{}),
    ...(input.onRequestStart?{onRequestStart:input.onRequestStart}:{}),
    ...(input.approve ? { approve: input.approve } : {}),
    ...(input.onToolReceipt ? { onToolReceipt: input.onToolReceipt } : {}),
    ...(input.onToolStart ? { onToolStart: input.onToolStart } : {}),
    ...(input.maxSteps == null ? {} : { maxSteps: input.maxSteps }),
    ...(input.signal ? { signal: input.signal } : {}),
    ...(input.sourceMessageIds?.length ? { sourceMessageIds: input.sourceMessageIds } : {}),
    onTextDelta: (delta, step) => input.onEvent?.({ type: "delta", delta, agentStep: step }),
    transport: async (turn, emit) => {
      const clientRequestId=crypto.randomUUID();
      input.onRequest?.({...input.request,messages:turn.messages});
      let currentToken = typeof input.accessToken === "function" ? await input.accessToken() : input.accessToken;
      if (!currentToken) {
        currentToken = (await getFreshAccessToken().catch(() => null)) || "";
      }
      const result = await streamNarration(input.endpoint, currentToken, {
        ...input.request,
        clientRequestId, messages: turn.messages,
        agent: { role: turn.role, enabledToolIds: turn.enabledToolIds, phase: turn.phase },
      }, (event) => {
        if (event.type !== "delta") input.onEvent?.({...event,clientRequestId});
        emit(event as AgentLoopEvent);
      }, input.signal);
      return { terminal: result.terminal as AgentLoopEvent | null };
    },

  });
}
